import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/transforms';

/**
 * Kolicky__2 (Pegs 2) - Multiple-choice with rotated options
 *
 * Layout: 3 landscape pages, 5×2 grid.
 * Page 1: 2 options, Page 2: 3 options, Page 3: 4 options.
 * Question label on top, rotated answer options on bottom strip.
 *
 * Migrated from C# EduFrame.Img10.Generators.Kolicky__2
 */
export class Kolicky__2 implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(5, 2, margin, PdfSize.A4H);
    const labels = opts.list.map((t) => t.right);
    const height = PdfUnit.FromMillimeters(30);
    const answerTexts = labels.map((l) => l.getText() ?? '');

    // Create all 3 pages upfront
    const pages = [pdf.addPage(true), pdf.addPage(true), pdf.addPage(true)];
    const labelFont = pages[0].adjustFont(height, answerTexts, GUtil.baseFont);

    let pageIndex = 0;
    for (let count = 2; count <= 4; count++) {
      const pg = pages[pageIndex++];
      const optionWidth = size.width / count;

      for (let x = 0; x < 5; x++) {
        for (let y = 0; y < 2; y++) {
          const ind = y * 5 + x;
          if (ind >= opts.list.length) continue;

          const rect = new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          );
          const r1 = new PdfRect(rect.x, rect.y, rect.width, rect.height - height);

          pg.drawLabel(opts.list[ind].left, { rect: r1 });
          pg.drawRectangle(GUtil.basePen, null, rect);

          const label = opts.list[ind].right;
          // Pick (count-1) random wrong answers + correct, shuffled
          const options = labels
            .filter((l) => l !== label)
            .sort(() => opts.data.random.next(0, 2) - 1)
            .slice(0, count - 1)
            .concat([label])
            .sort(() => opts.data.random.next(0, 2) - 1);

          for (let i = 0; i < options.length; i++) {
            const r = new PdfRect(
              rect.x + i * optionWidth,
              rect.bottom - height,
              optionWidth,
              height
            );
            const center = new PdfPoint(r.x + r.width / 2, r.y + r.height / 2);
            const r2 = new PdfRect(
              center.x - r.height / 2,
              center.y - r.width / 2,
              r.height,
              r.width
            );

            pg.save();
            pg.rotateAtTransform(270, center);
            pg.drawLabel(options[i], { rect: r2, font: labelFont });
            pg.restore();

            pg.drawRectangle(GUtil.basePen, null, r);
          }
        }
      }
    }
  }
}
